import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { SuggestionForm } from '../components/SuggestionForm';
import { useAuthStore } from '../lib/auth';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';

const KOFI_URL = import.meta.env['VITE_KOFI_URL'] as string | undefined;

export function SupportRoute() {
  const user = useAuthStore((s) => s.user);
  const rotateSupportCode = useAuthStore((s) => s.rotateSupportCode);
  const [supportCode, setSupportCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const initialType = params.get('type') === 'card_idea' ? 'card_idea' : 'bug';
  const gameId = params.get('gameId');
  const cardId = params.get('cardId');
  const context = {
    ...(gameId ? { gameId } : {}),
    ...(cardId ? { cardId } : {}),
  };

  async function makeCode() {
    const code = await rotateSupportCode();
    setSupportCode(code);
    setCopied(false);
  }

  async function copyCode() {
    if (!supportCode) return;
    await navigator.clipboard?.writeText(supportCode).catch(() => undefined);
    setCopied(true);
  }

  return (
    <div className="min-h-full w-full max-w-[1100px] mx-auto px-3 sm:px-5 md:px-10 py-4 sm:py-6 pb-12 flex flex-col gap-3 sm:gap-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Support the Site</h1>
        <p className="text-xs sm:text-sm text-ink-muted">
          No ads. No paywalls on gameplay. The site runs on you.
        </p>
      </div>
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-3 sm:gap-5">
        <Panel className="p-5 sm:p-6">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-accent font-semibold">
            One tip. Lifetime unlock.
          </div>
          <h2 className="mt-2 font-display text-xl sm:text-2xl font-bold">
            Drop any amount on Ko-fi
          </h2>
          <p className="mt-3 text-sm text-ink-muted leading-snug">
            Every card, every theme, every fix &mdash; they happen because supporters
            chip in. Tip any amount on Ko-fi and you unlock the Supporter Themes
            permanently. No subscription. No upsells.
          </p>

          <div className="mt-5 rounded-card border border-ink/10 bg-surface-2 p-4">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">
              What you get
            </div>
            <ul className="space-y-1.5 text-sm text-ink-muted">
              <li>· Unlock all Supporter Themes</li>
              <li>· Vote weight on upcoming card ideas <span className="text-ink-muted">(coming soon)</span></li>
              <li>· A supporter mark on your profile</li>
              <li>· You keep the site running for everyone</li>
            </ul>
          </div>

          {user ? (
            <div className="mt-5 space-y-3">
              <div className="rounded-md border border-ink/10 bg-surface-2 p-3">
                <div className="text-xs uppercase tracking-wider text-ink-muted">Your support code</div>
                <div className="mt-1 font-mono text-lg sm:text-xl font-semibold">
                  {supportCode ?? 'Make a code first'}
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Paste this code in your Ko-fi message so we can link your tip to your
                  account and unlock your themes automatically.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => void makeCode()} variant="secondary">
                  Make code
                </Button>
                <Button onClick={() => void copyCode()} variant="ghost" disabled={!supportCode}>
                  {copied ? 'Copied' : 'Copy code'}
                </Button>
                <a href={KOFI_URL || '#'} target="_blank" rel="noreferrer">
                  <Button type="button" disabled={!KOFI_URL}>
                    Support on Ko-fi
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <a href={KOFI_URL || '#'} target="_blank" rel="noreferrer">
                <Button size="lg" type="button" disabled={!KOFI_URL}>
                  Support on Ko-fi
                </Button>
              </a>
              <p className="text-xs text-ink-muted leading-snug">
                Logged-in players can grab a support code from this page and paste it in
                their Ko-fi message &mdash; that link their tip to their account so
                themes unlock automatically.{' '}
                <Link to="/login" className="text-accent hover:underline">
                  Log in
                </Link>{' '}
                or{' '}
                <Link to="/join" className="text-accent hover:underline">
                  create an account
                </Link>{' '}
                first if you want themes on your account.
              </p>
            </div>
          )}

          <p className="mt-5 text-xs text-ink-muted leading-snug">
            Monthly memberships with extra perks come later. For now any tip is a
            one-time, lifetime unlock.
          </p>
        </Panel>
        <div>
          <SuggestionForm initialType={initialType} context={context} />
        </div>
      </div>
    </div>
  );
}
